import type { Block, Cell, ChipCell, Column, Tone } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'

import type { ScreenData, ScreenLoader } from './provenance'
import {
  asArray, asRecord, attempt, disclosure, knows, malformed, readBool, readDay,
  readInstant, readInt, readText, receipt, refLine, spanOf, summarise,
  type Attempt,
} from './wire'

/**
 * Statement runs, the referral funnel and the code register (this console #161).
 *
 *   st-runs    GET /v1/admin/statement-runs
 *   rf-funnel  GET /v1/admin/referral/funnel
 *   rf-codes   GET /v1/admin/referral/codes
 *
 * Three screens from two sections, together because each is the only list read
 * its section still had unwired and neither would fill a module.
 *
 * # `st-runs` — a refused run issued nothing AND is missing nothing
 *
 * That is the state most likely to be misread. `refused` is not a failure: no
 * statement was issued and none is absent, so an operator seeing it must not go
 * looking for lost documents.
 *
 * The counts are the other trap. They are WHAT ONE BOUNDED PASS SAW, not the
 * month's totals — `at_batch_limit` says the pass stopped at its ceiling, which
 * makes every figure a floor — and `counted: false` means they were not
 * computed at all, which is a different thing from zero. There is also no
 * scheduled run and no row exists before a pass, so an empty list means nothing
 * has run rather than that nothing was due.
 *
 * Nothing here starts, retries or cancels a run. Invariant 12 is why the
 * neighbouring regenerate screen is not wired either: a regenerate produces a
 * NEW VERSION and never rewrites an issued statement, so its control must be
 * incapable of expressing "re-issue this" — a shape that belongs in a change
 * that designs it.
 *
 * # `rf-funnel` — §6.8.1 forbids any write, and two zeros are not zeros
 *
 * "Actions: none. There is no write on this endpoint and there must not be
 * one." So no acknowledge, no threshold edit, no pause, no recompute.
 *
 * A code with nothing in the window is ABSENT from the response, never present
 * with zeros — so the screen must not imply a missing code earned nothing. And
 * when the requested window starts before recording began, the counts are not
 * measurements of a quiet period; `window_precedes_recording` says so and it is
 * rendered as a caveat rather than a footnote.
 *
 * ACTIVATION IS NOT SERVED and there is no field for it. The design draws an
 * activation rung; it is dropped rather than derived from the rungs that exist.
 *
 * # `rf-codes` — state is derived, not stored
 *
 * There is no state column on a code. The word is computed from retirement and
 * suspension, so this screen renders those two underlying facts beside it
 * rather than only the conclusion.
 *
 * The funnel columns are NOT in this response and are NOT zero. Drawing them
 * here would report every code as earning nothing; they are on `rf-funnel`.
 *
 * # No money on any of the three
 *
 * No amount, no currency, no balance is on these wires — so nothing here
 * imports a formatter and nothing builds an amount cell.
 */

export const STATEMENT_RUNS_PATH = '/statement-runs'

export function statementPeriodPath(period: string): string {
  return `${STATEMENT_RUNS_PATH}/${encodeURIComponent(period)}`
}
export const REFERRAL_FUNNEL_PATH = '/referral/funnel'
export const REFERRAL_CODES_PATH = '/referral/codes'

const RUNS_SOURCE = 'postgres · statement_runs (GET /v1/admin/statement-runs)'
const FUNNEL_SOURCE = 'postgres · referral_funnel (GET /v1/admin/referral/funnel)'
const CODES_SOURCE = 'postgres · referral_codes (GET /v1/admin/referral/codes)'

const AUTHORITY = 'operational'

function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  return {
    blocks: [{
      t: 'note', tone: 'r', kicker, title,
      body: `${failed.reason} ${body}`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

// ===========================================================================
// st-runs
// ===========================================================================

/**
 * Run states.
 *
 * `refused` is NEUTRAL, not critical, and the label carries the whole point: no
 * statement was issued and none is missing. Drawn as a failure it would send an
 * operator hunting for documents that were never owed.
 *
 * `failed` is attention rather than critical: a statement pass that failed
 * blocks no money, and invariant 12's remedy is another pass rather than an
 * incident.
 */
const RUN_STATE: Readonly<Record<string, readonly [string, Tone]>> = {
  completed: ['completed', 'g'],
  refused: ['refused · issued nothing, and nothing is missing', 'n'],
  failed: ['failed · nothing was issued', 'a'],
  running: ['running', 'n'],
}

const RUN_COLS: readonly Column[] = [
  ['Run', 'm', '90px'],
  ['Period', 'm', 'minmax(170px,1.2fr)'],
  ['State', 'c', '230px'],
  ['Customers due', 'n', '130px'],
  ['Issued', 'n', '100px'],
  ['Unchanged', 'n', '116px'],
  ['Skipped', 'n', '104px'],
  ['Failed', 'n', '96px'],
  ['Not persisted', 'n', '126px'],
  ['Took', 'x', '110px'],
  ['Cell', 'm', '130px'],
]

/**
 * A count, or the two reasons there is not one.
 *
 * `counted: false` means the figures were NOT COMPUTED — different from zero,
 * and a zero here would report a month in which nothing was owed.
 */
function countCell(o: Record<string, unknown>, key: string, counted: boolean | null, gaps: string[]): Cell {
  if (counted === false) return '— not counted'
  const n = readInt(o, key)
  if (n.kind === 'exact') return String(n.value)
  gaps.push('a statement-run count')
  return '—'
}

export function composeStatementRuns(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Statement runs', 'The run history could not be read',
      'No pass below is known — that is different from no pass having run.',
      read, RUNS_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['rows'])
  if (o === null || list === null) {
    return unread(
      'Statement runs', 'The run history could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), RUNS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  // The PERIOD, not the run id: a row opens into every pass over that period,
  // which is the question an operator has when one pass looks wrong.
  const rowKeys: (string | null)[] = []
  let unreadable = 0
  let atLimit = 0
  let unpersisted = 0

  for (const raw of list) {
    const r = asRecord(raw)
    if (r === null) { unreadable += 1; continue }

    const id = readInt(r, 'id')
    const period = readText(r, 'period')
    rowKeys.push(period)
    const from = readDay(r, 'period_from')
    const to = readDay(r, 'period_to')
    const basis = readText(r, 'period_basis')
    if (period === null) gaps.push('a period')

    const state = readText(r, 'state')
    if (state === null) gaps.push('a run state')
    const known = state !== null && knows(RUN_STATE, state)
    if (state !== null && !known) gaps.push('a run state this console recognises')
    const label = known ? RUN_STATE[state] : undefined
    const stateCell: ChipCell = [label?.[0] ?? (state ?? 'unstated'), label?.[1] ?? 'a']

    const counted = readBool(r, 'counted')
    if (counted === null) gaps.push('whether a run’s counts were computed')

    const unp = readInt(r, 'documents_unpersisted')
    if (counted !== false && unp.kind === 'exact' && unp.value > 0) unpersisted += unp.value
    if (readBool(r, 'at_batch_limit') === true) atLimit += 1

    const ms = readInt(r, 'duration_ms')

    rows.push([
      id.kind === 'exact' ? String(id.value) : '— no id',
      // The period AND its basis: a run over a calendar month and one over the
      // institution's value dates are different windows with the same name.
      period === null
        ? '— not stated'
        : from !== null && to !== null
          ? `${period} · ${from} → ${to}${basis === null ? '' : ` (${basis})`}`
          : period,
      stateCell,
      countCell(r, 'customers_due', counted, gaps),
      countCell(r, 'statements_issued', counted, gaps),
      countCell(r, 'statements_unchanged', counted, gaps),
      countCell(r, 'statements_skipped', counted, gaps),
      countCell(r, 'statements_failed', counted, gaps),
      countCell(r, 'documents_unpersisted', counted, gaps),
      ms.kind === 'exact' ? spanOf(Math.floor(ms.value / 1000)) : '—',
      readText(r, 'cell_id') ?? '—',
    ])
  }

  // No row exists before a pass, and there is no scheduled run — so an empty
  // list means nothing has run, not that nothing was due.
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No statement pass has run. No row exists before a pass and none is scheduled, so this is “nothing has run” rather than “nothing was due”.'
        : `No pass could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'pass' : 'passes'} · counts are what each bounded pass saw, not a month’s totals`

  const blocks: Block[] = [{
    t: 'table', title: 'Statement passes', src: 'ledger', facets: false,
    cols: RUN_COLS, rows, footer,
  }]

  const capability = disclosure(o, 'capability_note', 'Refused', 'What a refused pass means', 'n')
  if (capability !== null && rows.length > 0) blocks.push(capability)
  const coverage = disclosure(o, 'coverage_note', 'Counts', 'What these figures cover', 'n')
  if (coverage !== null && rows.length > 0) blocks.push(coverage)
  const scheduling = disclosure(o, 'scheduling_note', 'Scheduling', 'Why an empty list is not a quiet month', 'n')
  if (scheduling !== null && rows.length === 0) blocks.push(scheduling)

  if (atLimit > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Batch limit',
      title: `${String(atLimit)} pass${atLimit === 1 ? '' : 'es'} stopped at the batch ceiling`,
      body: 'Every count on those rows is a floor rather than a total: the pass ran out of budget before it ran out of customers.',
      ref: 'at_batch_limit = true',
    })
  }

  if (unpersisted > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Not persisted',
      title: `${String(unpersisted)} document${unpersisted === 1 ? ' was' : 's were'} generated with nowhere to store them`,
      body: 'The statement was produced and the artifact was not written. No deployment has object storage configured, so this is the expected state rather than a fault to chase.',
      ref: 'documents_unpersisted',
    })
  }

  const missing = [
    'a start, retry or cancel — nothing on this surface runs a pass',
    'a re-issue control — a regenerate produces a new statement version and never rewrites an issued one, which is a control that has to be designed rather than inherited',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} run row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: RUNS_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Statement runs',
    subject: `statement_runs · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

export const loadStatementRuns: ScreenLoader = async (api) =>
  composeStatementRuns(await attempt(() => api.get<unknown>(STATEMENT_RUNS_PATH), 'The statement runs'), new Date())

// ===========================================================================
// rf-funnel — §6.8.1: actions, none
// ===========================================================================

/**
 * The columns.
 *
 * The design draws an ACTIVATION rung. It is not served and there is no field
 * for it, so it is dropped rather than derived from the rungs that exist.
 *
 * The rung labels say what they count. Clicks are distinct VISITORS and the
 * lower rungs are distinct CUSTOMERS, so these are not one population narrowing
 * — a ratio between them is not a conversion rate, and unlabelled columns invite
 * exactly that arithmetic.
 */
const FUNNEL_COLS: readonly Column[] = [
  ['Code', 'm', '130px'],
  ['Partner', 'x', 'minmax(180px,1.3fr)'],
  ['Standing', 'c', '150px'],
  ['Distinct visitors', 'n', '150px'],
  ['Distinct signups', 'n', '150px'],
  ['Verified', 'n', '116px'],
]

export function composeReferralFunnel(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Referral funnel', 'The funnel could not be read',
      'No code below is known — that is different from no code having been used.',
      read, FUNNEL_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['rows'])
  if (o === null || list === null) {
    return unread(
      'Referral funnel', 'The funnel could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), FUNNEL_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0

  for (const raw of list) {
    const r = asRecord(raw)
    if (r === null) { unreadable += 1; continue }

    const code = readText(r, 'code')
    if (code === null) gaps.push('a code')
    const label = readText(r, 'code_label')
    const partner = readText(r, 'partner_legal_name')
    const primary = readBool(r, 'is_primary')

    // Two facts behind one word, both rendered: a retired code and a suspended
    // one stopped earning for different reasons with different remedies.
    const retired = readBool(r, 'retired')
    const suspended = readBool(r, 'suspended')
    const standing: ChipCell = suspended === true
      ? ['suspended', 'a']
      : retired === true
        ? ['retired', 'n']
        : (retired === null || suspended === null) ? ['partly read', 'a'] : ['live', 'g']

    const clicks = readInt(r, 'clicks')
    const signups = readInt(r, 'signups')
    const verified = readInt(r, 'verified')
    if (clicks.kind !== 'exact') gaps.push('a visitor count')
    if (signups.kind !== 'exact') gaps.push('a signup count')
    if (verified.kind !== 'exact') gaps.push('a verified count')

    rows.push([
      label === null ? (code ?? '— unnamed') : `${code ?? '—'} · ${label}`,
      partner === null ? '—' : primary === true ? `${partner} · primary code` : partner,
      standing,
      clicks.kind === 'exact' ? String(clicks.value) : '—',
      signups.kind === 'exact' ? String(signups.value) : '—',
      verified.kind === 'exact' ? String(verified.value) : '—',
    ])
  }

  const from = readInstant(o, 'from')
  const to = readInstant(o, 'to')
  const precedes = readBool(o, 'window_precedes_recording')
  const since = readInstant(o, 'recorded_since')

  // A code with nothing in the window is ABSENT, never present with zeros, so
  // the footer must not let the row count read as "these are all the codes".
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No code saw a visitor in this window. A code with nothing in the window is absent from this read rather than listed at zero, so this is not a list of codes earning nothing.'
        : `No row could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'code' : 'codes'} with activity in this window${from.kind === 'at' && to.kind === 'at' ? ` · ${from.label} → ${to.label}` : ''} · a code with none is absent rather than zero`

  const blocks: Block[] = [{
    t: 'table', title: 'Referral funnel', src: 'ledger', facets: false,
    cols: FUNNEL_COLS, rows, footer,
  }]

  // The caveat that changes what every figure means. Not a footnote: if the
  // window starts before recording began, these counts are not measurements of
  // a quiet period.
  if (precedes === true) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Window',
      title: 'This window starts before recording began',
      body: `Counts for the earlier part of it are absences of data rather than absences of activity${since.kind === 'at' ? `. Recording starts ${since.label}` : ''}.`,
      ref: 'window_precedes_recording = true',
    })
  }

  const coverage = disclosure(o, 'coverage_note', 'Coverage', 'A code with nothing here is absent', 'n')
  if (coverage !== null && rows.length > 0) blocks.push(coverage)
  const activation = disclosure(o, 'activation_note', 'Activation', 'Why there is no activation rung', 'n')
  if (activation !== null) blocks.push(activation)
  const method = disclosure(o, 'method_note', 'What is counted', 'Visitors, then customers', 'n')
  if (method !== null && rows.length > 0) blocks.push(method)

  const missing = [
    'an activation rung — it is not served and there is no field for it, so it is not derived from the rungs that are',
    'any control — §6.8.1 permits no write on programme economics: no acknowledge, no threshold edit, no pause and no recompute. A suspension is a conduct decision with its own screen',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} funnel row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: FUNNEL_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Referral funnel',
    subject: `referral_funnel · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadReferralFunnel: ScreenLoader = async (api) =>
  composeReferralFunnel(await attempt(() => api.get<unknown>(REFERRAL_FUNNEL_PATH), 'The referral funnel'), new Date())

// ===========================================================================
// rf-codes — state is derived, not stored
// ===========================================================================

const CODE_STATE_TONE: Readonly<Record<string, Tone>> = {
  live: 'g', suspended: 'a', retired: 'n',
}

/**
 * The columns.
 *
 * The design draws clicks and signups here. They are NOT in this response and
 * are NOT zero — they are on `rf-funnel`, and drawing them here would report
 * every code as earning nothing.
 *
 * `Retired` and `Suspended` are rendered beside the derived state rather than
 * folded into it: the word is computed from those two facts, and an operator
 * asking WHY a code stopped earning needs the fact rather than the conclusion.
 */
const CODE_COLS: readonly Column[] = [
  ['Code', 'm', '130px'],
  ['Partner', 'x', 'minmax(180px,1.3fr)'],
  ['State', 'c', '124px'],
  ['Retired', 'm', '150px'],
  ['Suspended', 'x', 'minmax(190px,1.3fr)'],
  ['Created', 'm', '150px'],
]

export function composeReferralCodes(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Referral codes', 'The code register could not be read',
      'No code below is known — that is different from none being minted.',
      read, CODES_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['codes'])
  if (o === null || list === null) {
    return unread(
      'Referral codes', 'The code register could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), CODES_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let suspended = 0

  for (const raw of list) {
    const c = asRecord(raw)
    if (c === null) { unreadable += 1; continue }

    const code = readText(c, 'code')
    if (code === null) gaps.push('a code')
    const label = readText(c, 'label')
    const partner = readText(c, 'partner_name')
    const primary = readBool(c, 'is_primary')

    const state = readText(c, 'state')
    if (state === null) gaps.push('a code state')
    const known = state !== null && knows(CODE_STATE_TONE, state)
    if (state !== null && !known) gaps.push('a code state this console recognises')
    const stateCell: ChipCell = [state ?? 'unstated', known ? (CODE_STATE_TONE[state] ?? 'a') : 'a']

    // The facts the state was derived FROM. `disabled_at` absent is a live
    // code, which is a position rather than a gap.
    const disabled = readInstant(c, 'disabled_at')

    // A suspension is a conduct decision. Its reason is what an operator opened
    // this screen for; the lift control is on the conduct screen, not here.
    const susp = asRecord(c['suspension'])
    let suspensionCell = '— none'
    if (susp !== null) {
      suspended += 1
      const reason = readText(susp, 'reason_code') ?? readText(susp, 'reason')
      const by = readText(susp, 'suspended_by')
      suspensionCell = [reason, by === null ? null : `by ${by}`]
        .filter((s): s is string => s !== null).join(' · ') || 'suspended, reason not stated'
    }

    const created = readInstant(c, 'created_at')

    rows.push([
      label === null ? (code ?? '— unnamed') : `${code ?? '—'} · ${label}`,
      partner === null ? '—' : primary === true ? `${partner} · primary code` : partner,
      stateCell,
      disabled.kind === 'at' ? disabled.label : '— not retired',
      suspensionCell,
      created.kind === 'at' ? created.label : '—',
    ])
  }

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No referral code has been minted.'
        : `No code could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'code' : 'codes'}${suspended === 0 ? '' : ` · ${String(suspended)} suspended`} · state is derived from retirement and suspension, not stored`

  const blocks: Block[] = [{
    t: 'table', title: 'Referral codes', src: 'ledger', facets: false,
    cols: CODE_COLS, rows, footer,
  }]

  const stateNote = disclosure(o, 'state_note', 'State', 'Where the state comes from', 'n')
  if (stateNote !== null && rows.length > 0) blocks.push(stateNote)
  const funnelNote = disclosure(o, 'funnel_note', 'Funnel', 'Why there are no click counts here', 'n')
  if (funnelNote !== null && rows.length > 0) blocks.push(funnelNote)

  if (suspended > 0) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Lifting a suspension',
      title: 'The control for this is not on this screen',
      body: 'A suspension and its lift are conduct decisions with their own attributed screen. This register shows why a code stopped earning; it does not change it.',
      ref: '§6.8.1 · actions, none',
    })
  }

  const missing = [
    'click and signup counts — they are not on this read and are not zero; they are on the referral funnel, and drawing them here would report every code as earning nothing',
    'any control — nothing here mints, retires, suspends or lifts a code',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} code row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: CODES_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Referral codes',
    subject: `referral_codes · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadReferralCodes: ScreenLoader = async (api) =>
  composeReferralCodes(await attempt(() => api.get<unknown>(REFERRAL_CODES_PATH), 'The code register'), new Date())

// ---------------------------------------------------------------------------
// st-versions — one period's passes, opened from a run row
// ---------------------------------------------------------------------------

/**
 * Every pass over one billing period.
 *
 * # The screen is called "Versions & hashes" and this read has neither
 *
 * `st-versions` draws a content hash per issued statement and a version number
 * beside it. **Neither is on this wire.** What `/statement-runs/{period}`
 * returns is the passes made over that period — the same shape the list
 * returns, narrowed to one period — so the hash column is DROPPED rather than
 * filled, and the provenance says which.
 *
 * That is not a small omission and it is worth naming precisely: a content hash
 * is the evidence a statement is reproducible (invariant 12), so a screen
 * showing an invented one would be claiming exactly the property nobody had
 * checked. There is no rendition read on this surface at all.
 *
 * What the period read DOES answer is the question underneath the screen: how
 * many times this period was passed over, what each pass issued, and whether
 * any of them refused. A period passed twice with different counts is the row
 * an operator opens this for.
 */
const PERIOD_COLS: readonly Column[] = [
  ['Pass', 'm', '90px'],
  ['State', 'c', '230px'],
  ['Customers due', 'n', '130px'],
  ['Issued', 'n', '100px'],
  ['Unchanged', 'n', '116px'],
  ['Skipped', 'n', '104px'],
  ['Failed', 'n', '96px'],
  ['Not persisted', 'n', '126px'],
  ['Started', 'm', '150px'],
  ['Cell', 'm', '130px'],
]

export function composeStatementPeriod(read: Attempt, period: string, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Statement period', `The passes over ${period} could not be read`,
      'No pass below is known — that is different from the period never having been run.',
      read, RUNS_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['runs'])
  if (o === null || list === null) {
    return unread(
      'Statement period', `The passes over ${period} could not be read`,
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), RUNS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let issued = 0
  let counted = 0

  for (const raw of list) {
    const r = asRecord(raw)
    if (r === null) { unreadable += 1; continue }

    const id = readInt(r, 'id')
    const state = readText(r, 'state')
    if (state === null) gaps.push('a pass state')
    const known = state !== null && knows(RUN_STATE, state)
    if (state !== null && !known) gaps.push('a pass state this console recognises')
    const label = known ? RUN_STATE[state] : undefined
    const stateCell: ChipCell = [label?.[0] ?? (state ?? 'unstated'), label?.[1] ?? 'a']

    const wasCounted = readBool(r, 'counted')
    if (wasCounted === null) gaps.push('whether a pass’s counts were computed')
    const n = readInt(r, 'statements_issued')
    if (wasCounted !== false && n.kind === 'exact') { issued += n.value; counted += 1 }

    const started = readInstant(r, 'started_at')

    rows.push([
      id.kind === 'exact' ? String(id.value) : '— no id',
      stateCell,
      countCell(r, 'customers_due', wasCounted, gaps),
      countCell(r, 'statements_issued', wasCounted, gaps),
      countCell(r, 'statements_unchanged', wasCounted, gaps),
      countCell(r, 'statements_skipped', wasCounted, gaps),
      countCell(r, 'statements_failed', wasCounted, gaps),
      countCell(r, 'documents_unpersisted', wasCounted, gaps),
      started.kind === 'at' ? started.label : '—',
      readText(r, 'cell_id') ?? '—',
    ])
  }

  // A period passed more than once is the finding this screen opens for. The
  // total is only stated over the passes that were actually counted — summing
  // across an uncounted one would report a floor as a total.
  const footer = rows.length === 0
    ? (unreadable === 0
        ? `No pass has been made over ${period}. No row exists before a pass, so this is “never run” rather than “nothing was due”.`
        : `No pass could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'pass' : 'passes'} over ${period}${counted === rows.length ? ` · ${String(issued)} statements issued in total` : ` · ${String(issued)} issued across the ${String(counted)} pass(es) that were counted`}`

  const blocks: Block[] = [{
    t: 'table', title: `Passes over ${period}`, src: 'ledger', facets: false,
    cols: PERIOD_COLS, rows, footer,
  }]

  if (rows.length > 1) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Repeated',
      title: `${period} has been passed over ${String(rows.length)} times`,
      body: 'A statement is reissued as a new version rather than rewritten, so more than one pass is ordinary — but the counts differing between them is what to read here.',
      ref: 'invariant 12 · a regenerate is a new version',
    })
  }

  const capability = disclosure(o, 'capability_note', 'Refused', 'What a refused pass means', 'n')
  if (capability !== null && rows.length > 0) blocks.push(capability)

  const missing = [
    'a content hash per statement — the screen is titled for one and this read has no such field; a hash is the evidence a statement is reproducible, so an invented one would claim exactly the property nobody had checked',
    'a per-statement version number — no rendition read exists on this surface at all',
    'a re-issue control — a regenerate produces a new version and never rewrites an issued statement',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} pass row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: RUNS_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: `Statement period ${period}`,
    subject: `statement_runs · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

/** One period's passes, opened from a run row. */
export async function readStatementPeriod(api: ApiClient, period: string): Promise<ScreenData> {
  return composeStatementPeriod(
    await attempt(() => api.get<unknown>(statementPeriodPath(period)), `The passes over ${period}`),
    period, new Date(),
  )
}
